import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CreditCard } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { ColumnFilterBar } from '@/components/data-table/column-filter-bar';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { CoinCell } from '@/components/ui/coin-cell';
import { CopyButton } from '@/components/ui/copy-button';
import { PAYOUT_ACCOUNTS_QUERY_KEY, usePayoutAccounts } from '@/hooks/queries/funds/use-payout-accounts';
import { type ColumnFilterConfig, useColumnFilters } from '@/hooks/use-column-filters';
import { getErrorMessage } from '@/lib/api/error';
import { PAYOUT_ACCOUNT_STATE_CHOICES, type PayoutAccount, updatePayoutAccount } from '@/services/api/payout-accounts';
import { truncateWithEllipsis } from '@/utils/string';

const PAGE_SIZE = 50;
const BLOCKCHAIN_ADDRESS_PREFIX_LENGTH = 8;
const DEFAULT_FILTER_FIELD = 'payout_account_id';

const RawCell = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  return <>{String(value)}</>;
};
RawCell.displayName = 'RawCell';

// Fix: legacy rendered this as a v-html string with an inline onclick —
// first 8 chars + ellipsis, short addresses shown whole, full value via tooltip + copy.
const BlockchainAddressCell = ({ value }: { value: string | undefined }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" title={value}>
      {truncateWithEllipsis(value, BLOCKCHAIN_ADDRESS_PREFIX_LENGTH)}
      <CopyButton label={`Copy ${value}`} value={value} />
    </span>
  );
};
BlockchainAddressCell.displayName = 'BlockchainAddressCell';

type FilterKind = 'text' | 'choice' | 'none';

interface PayoutColumnConfig {
  field: string;
  title: string;
  filter: FilterKind;
  renderCell: (account: PayoutAccount) => ReactNode;
}

// Column order and titles. `account_holder` (PID) deliberately keeps a copy
// affordance only — the legacy screen did not link it to the user page.
const COLUMN_CONFIGS: PayoutColumnConfig[] = [
  { field: 'payout_account_id', title: 'Payout Acc ID', filter: 'text', renderCell: (account) => <UuidCell value={account.payout_account_id} /> },
  { field: 'currency', title: 'Currency', filter: 'choice', renderCell: (account) => <CoinCell asset={account.currency} /> },
  { field: 'account_holder', title: 'PID', filter: 'text', renderCell: (account) => <UuidCell value={account.account_holder} /> },
  { field: 'account_holder_name', title: 'Name', filter: 'text', renderCell: (account) => <RawCell value={account.account_holder_name} /> },
  { field: 'type', title: 'Type', filter: 'choice', renderCell: (account) => <RawCell value={account.type} /> },
  { field: 'state', title: 'State', filter: 'choice', renderCell: (account) => <RawCell value={account.state} /> },
  // Deliberate fix: legacy made `created` a multi-choice filter over exact
  // timestamps — one checkbox per row in practice. Filterability dropped instead.
  { field: 'created', title: 'Created', filter: 'none', renderCell: (account) => <DateCell value={account.created} /> },
  { field: 'iban', title: 'IBAN', filter: 'text', renderCell: (account) => <RawCell value={account.iban} /> },
  { field: 'blinc_id', title: 'BLINC ID', filter: 'text', renderCell: (account) => <RawCell value={account.blinc_id} /> },
  { field: 'address', title: 'Address', filter: 'text', renderCell: (account) => <RawCell value={account.address} /> },
  { field: 'country_code', title: 'Country', filter: 'choice', renderCell: (account) => <RawCell value={account.country_code} /> },
  { field: 'bic', title: 'BIC', filter: 'text', renderCell: (account) => <RawCell value={account.bic} /> },
  { field: 'blockchain_address', title: 'Blockchain Address', filter: 'text', renderCell: (account) => <BlockchainAddressCell value={account.blockchain_address} /> },
  { field: 'label', title: 'Label', filter: 'text', renderCell: (account) => <RawCell value={account.label} /> },
];

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

const toCellText = (value: unknown): string => (hasValue(value) ? String(value) : '');

interface ChangeStateDialogProps {
  account: PayoutAccount;
  onClose: () => void;
  onUpdated: () => Promise<void>;
}

const ChangeStateDialog = ({ account, onClose, onUpdated }: ChangeStateDialogProps) => {
  const [newState, setNewState] = useState(account.state);
  const [isUpdating, setIsUpdating] = useState(false);
  const isDirty = newState !== account.state;
  const isCurrentStateEditable = (PAYOUT_ACCOUNT_STATE_CHOICES as readonly string[]).includes(account.state);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      // Fix: updatePayoutAccount throws on failure inside onConfirm, which
      // keeps the justification dialog open showing the API error — legacy showed
      // nothing and always looked successful.
      const justification = await confirmAction({
        title: `Confirm Update Payout Account State to ${newState}`,
        description: `Proceeding with this action will update Payout Account ${account.payout_account_id} from ${account.state} to ${newState}. Your action here will be logged and will be auditable.`,
        actionLabel: `Update Payout Account State to ${newState}`,
        onConfirm: (justification) =>
          updatePayoutAccount({
            // The legacy Nuxt route merged the PID route param into the body —
            // here it comes from the edited row. Audit-only but required.
            PID: account.account_holder,
            payoutAccountId: account.payout_account_id,
            newState,
            comment: justification,
          }),
      });
      if (justification === null) return;
      toast.success(`Payout account state updated to ${newState}`);
      await onUpdated();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="size-5 shrink-0" />
            <span className="break-all">Change State of Payout Account {account.payout_account_id}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payout-account-new-state">Update payout account status to ...</Label>
          <Select onValueChange={setNewState} value={newState}>
            <SelectTrigger id="payout-account-new-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* A state outside the editable set displays as the current selection but
                  cannot be re-selected — legacy behaviour, options list unchanged.
                  Empty-state guard: Radix throws on an empty SelectItem value. */}
              {isCurrentStateEditable || !account.state ? null : (
                <SelectItem disabled value={account.state}>
                  {account.state}
                </SelectItem>
              )}
              {PAYOUT_ACCOUNT_STATE_CHOICES.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {choice}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button disabled={isUpdating} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button className="gap-2" disabled={!isDirty || isUpdating} onClick={handleUpdate} type="button" variant="primary">
            {isUpdating ? <Spinner className="size-4" /> : null}
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ChangeStateDialog.displayName = 'ChangeStateDialog';

export interface PayoutAccountsPanelProps {
  /** Scope the table to one user's accounts (user-console embedding). */
  accountHolderId?: string;
  /** `created` range — the API applies it only when BOTH are set. */
  fromDate?: Date;
  toDate?: Date;
}

export const PayoutAccountsPanel = ({ accountHolderId, fromDate, toDate }: PayoutAccountsPanelProps) => {
  const queryClient = useQueryClient();
  const query = usePayoutAccounts({ accountHolderId, fromDate, toDate });
  const accounts = query.data;

  const [editingAccount, setEditingAccount] = useState<PayoutAccount | null>(null);

  // Columns render only when at least one row carries the field; with no
  // data yet, all columns show so skeleton rows get a full header.
  const visibleConfigs = useMemo(() => {
    if (!accounts || accounts.length === 0) return COLUMN_CONFIGS;
    return COLUMN_CONFIGS.filter((config) => accounts.some((account) => hasValue(account[config.field])));
  }, [accounts]);

  const columns = useMemo(
    (): ColumnDef<PayoutAccount, unknown>[] =>
      visibleConfigs.map((config) => ({
        accessorKey: config.field,
        header: config.title,
        cell: ({ row }) => config.renderCell(row.original),
      })),
    [visibleConfigs],
  );

  // Filterable configs feed the shared column-filter-bar; getText re-uses
  // the same generic `account[field]` -> toCellText lookup applyFilters used inline.
  const filterConfigs = useMemo(
    (): ColumnFilterConfig<PayoutAccount>[] =>
      visibleConfigs
        .filter((config): config is PayoutColumnConfig & { filter: 'text' | 'choice' } => config.filter !== 'none')
        .map((config) => ({
          field: config.field,
          title: config.title,
          filter: config.filter,
          getText: (account: PayoutAccount) => toCellText(account[config.field]),
        })),
    [visibleConfigs],
  );

  const filterState = useColumnFilters(accounts, filterConfigs, DEFAULT_FILTER_FIELD);
  const filteredAccounts = filterState.filteredData;

  const handleUpdated = async () => {
    // On success close the edit dialog and refetch the list (the query key
    // already carries the current date range, so invalidation reuses it)
    setEditingAccount(null);
    await queryClient.invalidateQueries({ queryKey: PAYOUT_ACCOUNTS_QUERY_KEY });
  };

  // Fix: legacy stored `_networkError` and rendered nothing — show the
  // failure with a retry instead.
  if (query.isError) {
    return <QueryErrorBanner message={`Failed to load payout accounts: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <ColumnFilterBar configs={filterConfigs} idPrefix="payout" state={filterState} />

      <DataTable
        columns={columns}
        data={filteredAccounts}
        emptyMessage="No results"
        getRowId={(account) => account.payout_account_id}
        isLoading={query.isPending}
        onRowClick={setEditingAccount}
        pageSize={PAGE_SIZE}
      />

      {editingAccount ? <ChangeStateDialog account={editingAccount} onClose={() => setEditingAccount(null)} onUpdated={handleUpdated} /> : null}
    </div>
  );
};
PayoutAccountsPanel.displayName = 'PayoutAccountsPanel';
